import {
  AnyPromotionInfo,
  DiscountProductInfo,
  isSamePlanIgnoringEcom,
} from '../../../../models';

/**
 * Package promotion ("Starter package" in the Billing 2.0 design): a
 * single-product campaign entry, presented in one of two flavors by shape:
 *
 * - `onetime`: the discounted product carries `onetimeDiscountAmount`
 *   (e.g. trueAutoFirstFive — 5 True Automation seats for $4,000/yr). The BE
 *   grants the one-time amount only when the purchase redeems the FULL
 *   `maxDiscountLicense` quota (OrderPreviewServiceImpl.getDiscountAmount), so
 *   the offer is an all-or-nothing package sold through the claim-offer band.
 *
 * - `tiered`: a recurring per-seat discount capped at `maxDiscountLicense`
 *   (e.g. kse first-3 — first 3 KSE licenses at $1,000/yr, $1,800/yr from the
 *   4th). Every seat up to the cap is discounted independently, so the row
 *   advertises the tier pricing directly with no claim band.
 *
 * Detection is shape-based — any campaign code and any plan code with one of
 * these configs gets the starter-package presentation, no FE change needed for
 * future campaigns.
 */
export type StarterPackageOfferKind = 'onetime' | 'tiered';

export interface StarterPackageOffer {
  kind: StarterPackageOfferKind;
  campaignCode: string;
  /** Campaign product code the offer discounts (as returned by the BE). */
  productCode: string;
  /** Seats the offer discounts (remaining campaign quota). */
  seatCount: number;
  /** Per-seat recurring discount of the entry. */
  perUnitDiscount: number;
  /** The all-or-nothing package discount (0 for tiered offers). */
  onetimeDiscountAmount: number;
}

/**
 * The starter-package offer for a product row, or undefined when the active
 * promotion is not a package campaign / does not discount this product.
 * Default matching is ecom-insensitive, mirroring how preview responses are
 * joined back to requests; pass `matchExactCode` with the exact plan code the
 * BE will charge to mirror the BE's exact campaign matching instead
 * (SingleProductPromotionHandler) — e.g. the KSE ecom code.
 *
 * `GET /v2/promotion/active` only returns the campaign while the account is
 * eligible (dates, blacklist, first-time-buyer rules are BE-enforced). It stamps
 * `discountQuantity` with the account's REMAINING quota and `discountAmount`
 * with the per-seat discount times that quota. Since the one-time amount only
 * applies at full quota, a partially redeemed account can never receive that
 * package again — no onetime offer is surfaced for it. A tiered offer keeps
 * discounting the remaining seats, so it stays up (sized to what is left).
 */
export const getStarterPackageOffer = (
  activePromotion: AnyPromotionInfo | null,
  productCode: string,
  matchExactCode = false,
): StarterPackageOffer | undefined => {
  if (!activePromotion || !('discountedProducts' in activePromotion)) {
    return undefined;
  }

  const discounted = activePromotion.discountedProducts.find(
    product => (matchExactCode
      ? product.productCode === productCode
      : isSamePlanIgnoringEcom(product.productCode, productCode)),
  );
  if (!discounted || discounted.discountQuantity <= 0) {
    return undefined;
  }

  const onetimeDiscountAmount = discounted.onetimeDiscountAmount ?? 0;
  const perUnitDiscount = discounted.discountAmount / discounted.discountQuantity;
  if (onetimeDiscountAmount > 0) {
    if (discounted.discountQuantity < activePromotion.maxDiscountLicense) {
      return undefined;
    }

    return {
      kind: 'onetime',
      campaignCode: activePromotion.campaignCode,
      productCode: discounted.productCode,
      seatCount: activePromotion.maxDiscountLicense,
      perUnitDiscount,
      onetimeDiscountAmount,
    };
  }

  if (perUnitDiscount <= 0) {
    return undefined;
  }

  return {
    kind: 'tiered',
    campaignCode: activePromotion.campaignCode,
    productCode: discounted.productCode,
    seatCount: discounted.discountQuantity,
    perUnitDiscount,
    onetimeDiscountAmount: 0,
  };
};

/**
 * Flat package total: seatCount seats at the listed price minus the campaign
 * discounts. For tiered offers this is the "first N seats" total at the
 * discounted rate.
 */
export const getStarterPackagePrice = (
  offer: StarterPackageOffer,
  listedUnitPrice: number,
): number => offer.seatCount * (listedUnitPrice - offer.perUnitDiscount)
  - offer.onetimeDiscountAmount;

/**
 * Per-seat price inside the package: blended for onetime offers ($4,000 / 5 =
 * $800 "From" price), the discounted tier rate for tiered offers ($1,800 - $800
 * = $1,000 "From" price).
 */
export const getStarterPackageUnitPrice = (
  offer: StarterPackageOffer,
  listedUnitPrice: number,
): number => getStarterPackagePrice(offer, listedUnitPrice) / offer.seatCount;

/**
 * Starter-package figures for an order-summary line, derived from the preview the
 * BE actually priced. The preview's applied promotion does not carry the campaign
 * config, so the row is joined back to the active campaign by campaign + product
 * code. All amounts come from the preview product, so the summary always agrees
 * with what checkout will charge (the BE zeroes the onetime discount below the
 * package size — those rows simply render without the package lines).
 */
export interface StarterPackageSummaryLine {
  kind: StarterPackageOfferKind;
  /** Seats billed inside the package (preview's applied discount quantity). */
  packageSeats: number;
  /** What the package seats cost after the campaign discount. */
  packagePrice: number;
  /** Discounted per-seat rate of the package seats. */
  packageUnitPrice: number;
  /** Seats beyond the package, billed at the standard price. */
  additionalSeats: number;
  /** Standard per-seat price the additional seats bill at. */
  additionalUnitPrice: number;
  /** Total for the additional seats. */
  additionalPrice: number;
}

export const getStarterPackageSummaryLine = (
  activePromotion: AnyPromotionInfo | null,
  appliedPromotion: DiscountProductInfo | undefined,
  quantity: number,
  subTotal: number,
): StarterPackageSummaryLine | undefined => {
  if (
    !appliedPromotion
    || appliedPromotion.discountAmount <= 0
    || appliedPromotion.discountQuantity <= 0
    || quantity <= 0
    || subTotal <= 0
  ) {
    return undefined;
  }

  const offer = getStarterPackageOffer(activePromotion, appliedPromotion.productCode);
  if (!offer || offer.campaignCode !== appliedPromotion.campaignCode) {
    return undefined;
  }

  const packageSeats = Math.min(appliedPromotion.discountQuantity, quantity);
  const unitPrice = subTotal / quantity;
  const packagePrice = packageSeats * unitPrice - appliedPromotion.discountAmount;
  const additionalSeats = quantity - packageSeats;
  return {
    kind: offer.kind,
    packageSeats,
    packagePrice,
    packageUnitPrice: packagePrice / packageSeats,
    additionalSeats,
    additionalUnitPrice: unitPrice,
    additionalPrice: additionalSeats * unitPrice,
  };
};
